/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkHideEntityHits,
  bulkUnhideEntityHits,
  fetchEntityScreening,
  fetchEntityScreeningHistory,
  getDatabaseScreeningStatus,
  getScreeningHistoryByEntityId,
  getScreeningHistoryByProductId,
  getScreeningResultsByDatabaseId,
  getScreeningResultsForGroup,
  getScreeningRunsByDatabaseId,
  runDatabaseScreening,
  runEntityScreening,
  runEntityServiceDatabaseScreening,
  runProductCheck,
  runProductCheckSingle,
  runRetryScreeningForEntities,
  runTariffCheck,
  toggleEntityDocVisibility,
  toggleProductDocVisibility,
  IBulkHideEntityHitsRequest,
} from './screening.service';
import {
  IEntityScreening,
  IEntityScreeningHistoryResponse,
  IProductScreeningHistoryResponse,
  IRunEntityScreeningRequest,
  IRunProductCheckRequest,
  IRunProductScreeningRequest,
  IRunScreeningRequest,
  IRunTariffCheckRequest,
  IScreeningResultRow
} from './screening.d';
import { Time } from '../shared';
import { entityKeys } from '../entities/entities.keys';

// Query Keys
export const screeningKeys = {
  all: [ 'screening' ] as const,
  byDatabase: (databaseId: string) =>
    [ ...screeningKeys.all, 'database', databaseId ] as const,
  byGroup: (groupId: string) =>
    [ ...screeningKeys.all, 'group', groupId ] as const,
  entity: (entityData: IEntityScreening) =>
    [ ...screeningKeys.all, 'entity', entityData ] as const,
  entityHistory: (databaseId: string, groupId: string, entityName: string) =>
    [
      ...screeningKeys.all,
      'entityHistory',
      databaseId,
      groupId,
      entityName,
    ] as const,
  entityHistoryById: (databaseEntityId: string) =>
    [ ...screeningKeys.all, 'entityHistoryById', databaseEntityId ] as const,
  productHistory: (databaseId: string, groupId: string, productName: string) =>
    [
      ...screeningKeys.all,
      'productHistory',
      databaseId,
      groupId,
      productName,
    ] as const,
  productHistoryById: (databaseProducttId: string) =>
    [ ...screeningKeys.all, 'productHistoryById', databaseProducttId ] as const,
};

/**
 * Hook to get screening history for a specific database
 * @param {string} databaseId - The ID of the database to get screening results for
 * @returns {object} React Query result object
 */
export const useGetScreeningHistoryByDatabaseId = (databaseId?: string) => {
  return useQuery({
    queryKey: databaseId ? screeningKeys.byDatabase(databaseId) : [],
    queryFn: () => getScreeningResultsByDatabaseId(databaseId!),
    enabled: Boolean(databaseId),
    staleTime: 0, // 5 minutes
    gcTime: 30000,
    select: (data) => data.result as IScreeningResultRow[],
  });
};

/**
 * Hook to get screening results for a group
 * @param {string} groupId - The ID of the group to get screening results for
 * @returns {object} React Query result object
 */
export const useGetScreeningResultsForGroup = (groupId?: string) => {
  return useQuery({
    queryKey: groupId ? screeningKeys.byGroup(groupId) : [],
    queryFn: () => getScreeningResultsForGroup(groupId!),
    enabled: Boolean(groupId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => data.result as IScreeningResultRow[],
  });
};

/**
 * Hook to get entity screening history with group and value parameters
 * @param {string} databaseId - The database ID
 * @param {string} groupId - The group ID
 * @param {string} entityName - The entity name to search for
 * @returns {object} React Query result object
 */
export const useGetEntityScreeningHistory = (
  databaseId?: string,
  groupId?: string,
  entityName?: string,
) => {
  return useQuery({
    queryKey:
      databaseId && groupId && entityName ?
        screeningKeys.entityHistory(databaseId, groupId, entityName) :
        [],
    queryFn: () =>
      fetchEntityScreeningHistory({
        database_id: databaseId!,
        group_id: groupId!,
        entity_name: entityName!,
      }),
    enabled: Boolean(databaseId && groupId && entityName),
    staleTime: 10 * Time.MINUTE,
    gcTime: 20 * Time.MINUTE,
  });
};

/**
 * Hook to fetch entity screening data
 * @returns {object} React Query mutation object
 */
export const useFetchEntityScreening = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (screeningData: IEntityScreening) =>
      fetchEntityScreening(screeningData),
    onSuccess: (data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: screeningKeys.byDatabase(variables.database_id),
      });
      queryClient.invalidateQueries({
        queryKey: screeningKeys.entity(variables),
      });
      // Invalidate entity history queries for this database
      queryClient.invalidateQueries({
        queryKey: [
          ...screeningKeys.all,
          'entityHistory',
          variables.database_id,
        ],
      });
    },
  });
};

/**
 * Hook to run database screening
 * @returns {object} React Query mutation object
 */
export const useRunDatabaseScreening = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (screeningRequest: IRunScreeningRequest) =>
      runDatabaseScreening(screeningRequest),
    onSuccess: (data, variables) => {
      // Invalidate screening results for all affected databases
      variables.databaseIds.forEach((databaseId) => {
        queryClient.invalidateQueries({
          queryKey: screeningKeys.byDatabase(String(databaseId)),
        });
        // Invalidate entity history queries for this database
        queryClient.invalidateQueries({
          queryKey: [ ...screeningKeys.all, 'entityHistory', String(databaseId) ],
        });
      });

      // Invalidate group queries (we don't know which group, so invalidate all)
      queryClient.invalidateQueries({
        queryKey: screeningKeys.all,
      });
    },
  });
};

/**
 * Hook to run database screening for an entity database through the entity service.
 * @returns {object} React Query mutation object
 */
export const useRunEntityServiceDatabaseScreening = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { databaseId: string; screenedBy?: string }) =>
      runEntityServiceDatabaseScreening(params),
    onSuccess: (_data, variables) => {
      const databaseId = String(variables.databaseId);
      queryClient.invalidateQueries({
        queryKey: screeningKeys.byDatabase(databaseId),
      });
      // Invalidate entity history queries for this database
      queryClient.invalidateQueries({
        queryKey: [ ...screeningKeys.all, 'entityHistory', databaseId ],
      });
      queryClient.invalidateQueries({
        queryKey: screeningKeys.all,
      });
    },
  });
};

/**
 * Hook to retry screening for specific failed entities via the select-entities endpoint.
 * Uses DatabaseEntity IDs (de.id) from failed_record_ids in screening error metadata.
 */
export const useRunRetryScreening = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: runRetryScreeningForEntities,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: screeningKeys.byDatabase(variables.databaseId),
      });
      queryClient.invalidateQueries({
        queryKey: screeningKeys.all,
      });
    },
  });
};

/**
 * Hook to run single or multiple entity screenings
 * @returns {object} React Query mutation object
 */
export const useRunEntityScreening = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      screeningRequest:
        | IRunEntityScreeningRequest
        | IRunEntityScreeningRequest[],
    ) => runEntityScreening(screeningRequest),
    onSuccess: (data, variables) => {
      const payloads = Array.isArray(variables) ? variables : [ variables ];

      const databaseIds = Array.from(
        new Set(
          payloads
            .map((payload) => payload.databaseId)
            .filter((id): id is string | number => Boolean(id)),
        ),
      );

      databaseIds.forEach((databaseId) => {
        const idAsString = String(databaseId);
        queryClient.invalidateQueries({
          queryKey: screeningKeys.byDatabase(idAsString),
        });
        queryClient.invalidateQueries({
          queryKey: [ ...screeningKeys.all, 'entityHistory', idAsString ],
        });
      });

      queryClient.invalidateQueries({
        queryKey: screeningKeys.all,
      });
    },
  });
};

/**
 * Hook to run single or multiple entity screenings
 * @returns {object} React Query mutation object
 */
export const useRunProductScreening = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      screeningRequest: IRunProductScreeningRequest,
    ) => runProductCheckSingle(screeningRequest),
    onSuccess: (data, variables) => {
      const payloads = Array.isArray(variables) ? variables : [ variables ];

      const databaseIds = Array.from(
        new Set(
          payloads
            .map((payload) => payload.databaseId)
            .filter((id): id is string | number => Boolean(id)),
        ),
      );

      databaseIds.forEach((databaseId) => {
        const idAsString = String(databaseId);
        queryClient.invalidateQueries({
          queryKey: screeningKeys.byDatabase(idAsString),
        });
        queryClient.invalidateQueries({
          queryKey: [
            ...screeningKeys.all,
            'entityHistory',
            idAsString,
          ],
        });
      });

      queryClient.invalidateQueries({
        queryKey: screeningKeys.all,
      });
    },
  });
};

export const useRunTariffCheck = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (checkRequest: IRunTariffCheckRequest) =>
      runTariffCheck(checkRequest),
    onSuccess: (data, variables) => {
      // Invalidate queries to refresh the history table and status for affected databases
      variables.databaseIds.forEach((databaseId) => {
        const idAsString = String(databaseId);

        // Refreshes screening history/runs list for this DB
        queryClient.invalidateQueries({
          queryKey: screeningKeys.byDatabase(idAsString),
        });

        // Explicitly refresh status if using specific keys for progress bars
        queryClient.invalidateQueries({
          queryKey: [ ...screeningKeys.byDatabase(idAsString), 'status' ],
        });
      });

      // General invalidation to be safe
      queryClient.invalidateQueries({
        queryKey: screeningKeys.all,
      });
    },
  });
};

export const useRunProductCheck = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (checkRequest: IRunProductCheckRequest) =>
      runProductCheck(checkRequest),
    onSuccess: (data, variables) => {
      // Invalidate queries to refresh the history table and status for affected databases
      variables.databaseIds.forEach((databaseId) => {
        const idAsString = String(databaseId);

        // Refreshes screening history/runs list for this DB
        queryClient.invalidateQueries({
          queryKey: screeningKeys.byDatabase(idAsString),
        });

        // Explicitly refresh status if using specific keys for progress bars
        queryClient.invalidateQueries({
          queryKey: [ ...screeningKeys.byDatabase(idAsString), 'status' ],
        });
      });

      // General invalidation to be safe
      queryClient.invalidateQueries({
        queryKey: screeningKeys.all,
      });
    },
  });
};

/**
 * Hook to toggle entity document visibility
 * @returns {object} React Query mutation object
 */
export const useToggleEntityDocVisibility = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentId,
      itemId,
      reason,
      itemName,
    }: {
      parentId: string;
      itemId: string;
      reason?: string;
      itemName?: string;
    }) => toggleEntityDocVisibility(parentId, itemId, reason, itemName),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: screeningKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: entityKeys.all(),
      });
      queryClient.invalidateQueries({
        queryKey: [ 'comment' ],
      });
      queryClient.invalidateQueries({
        queryKey: [ 'audit' ],
      });
    },
  });
};

export const useBulkHideEntityHits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      selectedTeamId,
    }: {
      payload: IBulkHideEntityHitsRequest;
      selectedTeamId: string;
    }) => bulkHideEntityHits(payload, selectedTeamId),
    onSuccess: (_, variables) => {
      variables.payload.entities.forEach((entity) => {
        queryClient.invalidateQueries({
          queryKey: screeningKeys.entityHistoryById(
            entity.database_entity_id,
          ),
        });
      });
      queryClient.invalidateQueries({ queryKey: entityKeys.all() });
      queryClient.invalidateQueries({ queryKey: [ 'comment' ] });
      queryClient.invalidateQueries({ queryKey: [ 'audit' ] });
    },
  });
};

export const useBulkUnhideEntityHits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      selectedTeamId,
    }: {
      payload: IBulkHideEntityHitsRequest;
      selectedTeamId: string;
    }) => bulkUnhideEntityHits(payload, selectedTeamId),
    onSuccess: (_, variables) => {
      variables.payload.entities.forEach((entity) => {
        queryClient.invalidateQueries({
          queryKey: screeningKeys.entityHistoryById(
            entity.database_entity_id,
          ),
        });
      });
      queryClient.invalidateQueries({ queryKey: entityKeys.all() });
      queryClient.invalidateQueries({ queryKey: [ 'comment' ] });
      queryClient.invalidateQueries({ queryKey: [ 'audit' ] });
    },
  });
};

/**
 * Hook to toggle product document visibility
 * @returns {object} React Query mutation object
 */
export const useToggleProductDocVisibility = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentId,
      itemId,
      reason,
      itemName,
    }: {
      parentId: string;
      itemId: string;
      reason?: string;
      itemName?: string;
    }) => toggleProductDocVisibility(parentId, itemId, reason, itemName),
    onSuccess: () => {
      // Invalidate all screening queries as document visibility affects results
      queryClient.invalidateQueries({
        queryKey: screeningKeys.all,
      });

      // Invalidate product queries to refresh hidden_ids
      queryClient.invalidateQueries({
        queryKey: [ 'product' ],
      });

      queryClient.invalidateQueries({
        queryKey: [ 'comment' ],
      });
      queryClient.invalidateQueries({
        queryKey: [ 'audit' ],
      });
    },
  });
};

/**
 * Hook to get the latest screening result for a database
 * @param {string} databaseId - The ID of the database
 * @returns {object} React Query result object with the latest screening result
 */
export const useGetLatestScreeningResult = (databaseId?: string) => {
  const { data, ...rest } = useGetScreeningHistoryByDatabaseId(databaseId);

  return {
    data: data && data.length > 0 ? data[0] : null,
    ...rest,
  };
};

/**
 * Hook to check if a database has any screening results
 * @param {string} databaseId - The ID of the database
 * @returns {object} React Query result object with boolean indicating if screening exists
 */
export const useHasScreeningHistory = (databaseId?: string) => {
  const { data, ...rest } = useGetScreeningHistoryByDatabaseId(databaseId);

  return {
    data: data ? data.length > 0 : false,
    ...rest,
  };
};

/**
 * Hook to get screening runs metadata for a specific database
 * @param {string} databaseId - The ID of the database to get screening runs for
 * @returns {object} React Query result object
 */
export const useGetScreeningRunsByDatabaseId = (
  databaseId?: string,
  options: { enabled?: boolean } = {},
) => {
  return useQuery({
    queryKey: databaseId ?
      [ ...screeningKeys.byDatabase(databaseId), 'runs' ] :
      [],
    queryFn: () => getScreeningRunsByDatabaseId(databaseId!),
    enabled: Boolean(databaseId) && (options.enabled ?? true),
    staleTime: 0, // Always fresh for real-time progress
    gcTime: 15000,
    select: (data) => data.result?.runs || [],
  });
};

/**
 * Hook to get live screening status (in-progress and chunk progress) for a database
 * @param {string} databaseId - The ID of the database to get screening status for
 * @returns {object} React Query result object with latest status and live updates
 */
export const useGetDatabaseScreeningStatus = (
  databaseId?: string,
  useEntityService = false,
) => {
  const aiPhaseFastPollUntilRef = React.useRef<number | null>(null);

  return useQuery({
    queryKey: databaseId
      ? [
        ...screeningKeys.byDatabase(databaseId),
        'status',
        useEntityService ? 'entity' : 'automations',
      ]
      : [],
    queryFn: async() => getDatabaseScreeningStatus(databaseId!, useEntityService),
    enabled: Boolean(databaseId),
    staleTime: 0,
    gcTime: 15000,
    refetchInterval: (query) => {
      const screeningData: any = query.state?.data;
      const statusResult = screeningData?.result || screeningData;
      const mainInProgress = statusResult?.inProgress === true;
      const aiInProgress = statusResult?.aiInProgress === true;
      const verificationInProgress =
        statusResult?.verificationInProgress === true;
      const aiStatus = statusResult?.aiStatus;
      const verificationStatus = statusResult?.verificationStatus;
      const now = Date.now();

      // Keep the normal fast cadence during main screening, throughout AI
      // evaluation and post-AI verification, and for a short handoff window
      // between phases.
      if (mainInProgress || aiInProgress || verificationInProgress) {
        aiPhaseFastPollUntilRef.current = now + 45 * Time.SECOND;
        return 5 * Time.SECOND;
      }

      if (aiStatus === 'complete' && verificationStatus === 'complete') {
        aiPhaseFastPollUntilRef.current = null;
        return 60 * Time.SECOND;
      }

      if (
        aiPhaseFastPollUntilRef.current &&
        now < aiPhaseFastPollUntilRef.current
      ) {
        return 5 * Time.SECOND;
      }

      return 60 * Time.SECOND;
    },
    // No polling from a hidden browser tab; the status refetches on focus.
    refetchIntervalInBackground: false,
    select: (data) => data.result
  });
};

/**
 * Hook to get screening history for a specific entity by database entity ID
 * @param {string} databaseEntityId - The database_entity_id (UUID) from the DatabaseEntity table
 * @returns {object} React Query result object with latest result and deltas
 */
export const useGetScreeningHistoryByEntityId = (databaseEntityId?: string) => {
  return useQuery({
    queryKey: databaseEntityId ? screeningKeys.entityHistoryById(databaseEntityId) : [],
    queryFn: () => getScreeningHistoryByEntityId(databaseEntityId!),
    enabled: Boolean(databaseEntityId),
    staleTime: 5 * Time.MINUTE,
    gcTime: 15 * Time.MINUTE,
    select: (data) => data.result as IEntityScreeningHistoryResponse | null,
  });
};

/**
 * Hook to get screening history for a specific entity by database entity ID
 * @param {string} databaseProductId - The database_entity_id (UUID) from the DatabaseEntity table
 * @returns {object} React Query result object with latest result and deltas
 */
export const useGetScreeningHistoryByProductId = (databaseProductId?: string) => {
  return useQuery({
    queryKey: databaseProductId ? screeningKeys.productHistoryById(databaseProductId) : [],
    queryFn: () => getScreeningHistoryByProductId(databaseProductId!),
    enabled: Boolean(databaseProductId),
    staleTime: 5 * Time.MINUTE,
    gcTime: 15 * Time.MINUTE,
    select: (data) => data.result as IProductScreeningHistoryResponse | null,
  });
};
